import { getVapidPublicKey, subscribePush, unsubscribePush } from './api';

// ─── Web Push subscription helpers ───────────────────────────────
// Bridges the browser Push API and the backend subscription store. The custom
// service worker (src/sw.ts) handles the actual `push` events.

/** Is web push usable in this browser/context (secure origin, SW, Push API)? */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Current notification permission, or 'unsupported'. */
export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Whether THIS browser/device currently has a push subscription. Push
 * registration is inherently per-device, so the Settings toggle reflects this
 * (the device's own subscription) rather than any shared server setting.
 */
export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return !!subscription;
}

/** VAPID public keys are base64url — convert to the Uint8Array the API wants. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // Back it with a concrete ArrayBuffer so it satisfies BufferSource.
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export class PushError extends Error {}

/**
 * Request permission, subscribe via the service worker, and register the
 * subscription with the backend. Throws PushError with a user-facing message on
 * any failure (unsupported, denied, not configured).
 */
export async function enablePush(): Promise<void> {
  if (!isPushSupported()) {
    throw new PushError('Push notifications are not supported in this browser.');
  }

  const { key, enabled } = await getVapidPublicKey();
  if (!enabled || !key) {
    throw new PushError('Push notifications are not configured on the server.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushError('Notification permission was not granted.');
  }

  const registration = await navigator.serviceWorker.ready;
  // Reuse an existing subscription if present, else create one.
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    }));

  await subscribePush(subscription.toJSON());
}

/**
 * Re-register an already-present browser subscription with the backend so it's
 * attributed to the signed-in user. Subscriptions created before login (or before
 * the server learned to attribute them) are stored unattributed, which breaks
 * per-user notification prefs like auto-review; calling this on authenticated app
 * load self-heals them. No-op if this device isn't subscribed. Best-effort.
 */
export async function syncPushSubscription(): Promise<void> {
  try {
    if (!isPushSupported() || Notification.permission !== 'granted') return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await subscribePush(subscription.toJSON());
  } catch {
    /* best-effort — a failed re-attribution must not affect app load */
  }
}

/** Unsubscribe locally and remove the subscription from the backend. */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  try {
    await subscription.unsubscribe();
  } finally {
    await unsubscribePush(endpoint).catch(() => {
      /* best-effort server cleanup */
    });
  }
}
